/* eslint-disable no-nested-ternary */
import { createSlice } from '@reduxjs/toolkit';
import { message as toaster } from 'antd';
import { API } from 'api';

const initialState = {};

export const vendorBillApprovalSlice = createSlice({
	name: 'vendorBillApprovalState',
	initialState,
	reducers: {},
});

const VENDOR_BILL_ID_REQUIRED_MSG = 'Vendor bill ID is required';

/**
 * Update Vendor Bill with Pending Approval (V1)
 * POST /api/v1/update
 * { update_type: "vendor_bill", data: { id, charge_fields..., remark, image_keys } }
 *
 * Charge changes are stored as pending in vendor_bills_audits table
 * and require explicit accept/reject.
 *
 * ⚠️ Blocking Rule: If there are already pending changes for this vendor bill,
 * new edits are blocked until those changes are accepted or rejected.
 *
 * ⚠️ Image Rules:
 * - image_keys is only allowed when tds_amount is one of the changed fields
 * - Only "tds_amount" key is accepted in image_keys object
 * - If tds_amount is not being changed but image_keys is sent → error
 */
export const updateVendorBillWithApprovalV1 =
	({ req_data, setErrors }) =>
	async () => {
		if (!req_data || !req_data.id) {
			toaster.error(VENDOR_BILL_ID_REQUIRED_MSG);
			return Promise.reject(new Error(VENDOR_BILL_ID_REQUIRED_MSG));
		}

		const data = {
			update_type: 'vendor_bill',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.status === 'success') {
				toaster.success(
					res?.data?.message ||
						res.message ||
						'Changes submitted for approval. They will be applied once accepted.'
				);
			} else if (res?.status === 'error') {
				const errorMsg = res?.error?.message || 'Failed to submit changes for approval';
				toaster.error(errorMsg);
			}
			return res;
		});
	};

/**
 * Update Vendor Bill with Pending Approval (V2)
 * POST /api/v2/update
 * { update_type: "vendor_bill", data: { id, charge_fields..., remark, image_keys } }
 *
 * Charge changes are stored as pending in vendor_bills_audits table
 * and require explicit accept/reject.
 *
 * ⚠️ Blocking Rule: If there are already pending changes for this vendor bill,
 * new edits are blocked until those changes are accepted or rejected.
 *
 * ⚠️ Image Rules:
 * - image_keys is only allowed when tds_amount is one of the changed fields
 * - Only "tds_amount" key is accepted in image_keys object
 * - If tds_amount is not being changed but image_keys is sent → error
 */
export const updateVendorBillWithApprovalV2 =
	({ req_data, setErrors }) =>
	async () => {
		if (!req_data || !req_data.id) {
			toaster.error(VENDOR_BILL_ID_REQUIRED_MSG);
			return Promise.reject(new Error(VENDOR_BILL_ID_REQUIRED_MSG));
		}

		const data = {
			update_type: 'vendor_bill',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.status === 'success') {
				toaster.success(
					res?.data?.message ||
						res.message ||
						'Changes submitted for approval. They will be applied once accepted.'
				);
			} else if (res?.status === 'error') {
				const errorMsg = res?.error?.message || 'Failed to submit changes for approval';
				toaster.error(errorMsg);
			}
			return res;
		});
	};

/**
 * Unified function to update vendor bill with approval (handles both v1 and v2)
 * @param {Object} params - Parameters object
 * @param {Object} params.req_data - Request data containing id and charge fields
 * @param {Function} params.setErrors - Error handler function
 * @param {boolean} params.useV2API - Whether to use v2 API (default: false)
 * @returns {Promise} API response
 */
export const updateVendorBillWithApproval =
	({ req_data, setErrors, useV2API = false }) =>
	async () => {
		if (useV2API) {
			return updateVendorBillWithApprovalV2({ req_data, setErrors })();
		}
		return updateVendorBillWithApprovalV1({ req_data, setErrors })();
	};

/**
 * Search Vendor Bill Field Changes (Audit Trail) V1
 * POST /api/v1/search
 * { search_type: "vendor_bill_field_changes", filter_by: { id, status }, pagination: {...} }
 */
export const searchVendorBillFieldChangesV1 =
	({ id, status, pagination, setErrors }) =>
	async () => {
		if (!id) {
			toaster.error(VENDOR_BILL_ID_REQUIRED_MSG);
			return Promise.reject(new Error(VENDOR_BILL_ID_REQUIRED_MSG));
		}

		const filter_by = { id };
		if (status) {
			filter_by.status = status;
		}

		const data = {
			search_type: 'vendor_bill_field_changes',
			filter_by,
		};

		if (pagination) {
			data.pagination = pagination;
		}

		const CREDENTIALS = {
			url: '/search',
			data,
			setErrors,
		};

		return API.common(CREDENTIALS).then((response) => ({
			data: response?.data || [],
			rows: response?.data || [],
			meta: response?.meta || {},
		}));
	};

/**
 * Search Vendor Bill Field Changes (Audit Trail) V2
 * POST /api/v2/search
 * { search_type: "vendor_bill_field_changes", filter_by: { id, status }, pagination: {...} }
 */
export const searchVendorBillFieldChangesV2 =
	({ id, status, pagination, setErrors }) =>
	async () => {
		if (!id) {
			toaster.error(VENDOR_BILL_ID_REQUIRED_MSG);
			return Promise.reject(new Error(VENDOR_BILL_ID_REQUIRED_MSG));
		}

		const filter_by = { id };
		if (status) {
			filter_by.status = status;
		}

		const data = {
			search_type: 'vendor_bill_field_changes',
			filter_by,
		};

		if (pagination) {
			data.pagination = pagination;
		}

		const CREDENTIALS = {
			url: '/search',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => ({
			data: response?.data || [],
			rows: response?.data || [],
			meta: response?.meta || {},
		}));
	};

/**
 * Unified function to search vendor bill field changes (handles both v1 and v2)
 * @param {Object} params - Parameters object
 * @param {number|string} params.id - Vendor bill ID (required)
 * @param {string} params.status - Filter by status: "pending", "accepted", "rejected" (optional)
 * @param {Object} params.pagination - Pagination object with page and per_page (optional)
 * @param {Function} params.setErrors - Error handler function (optional)
 * @param {boolean} params.useV2API - Whether to use v2 API (default: false)
 * @returns {Promise} API response
 */
export const searchVendorBillFieldChanges =
	({ id, status, pagination, setErrors, useV2API = false }) =>
	async () => {
		if (useV2API) {
			return searchVendorBillFieldChangesV2({ id, status, pagination, setErrors })();
		}
		return searchVendorBillFieldChangesV1({ id, status, pagination, setErrors })();
	};

/**
 * Search Invoice Payments V1
 * POST /api/v1/search
 * { search_type: "invoice_payments", filter_by: { vendor_bill_id }, pagination: {...} }
 */
export const searchInvoicePaymentsV1 =
	({ vendor_bill_id, pagination, setErrors }) =>
	async () => {
		if (!vendor_bill_id) {
			toaster.error(VENDOR_BILL_ID_REQUIRED_MSG);
			return Promise.reject(new Error(VENDOR_BILL_ID_REQUIRED_MSG));
		}

		const filter_by = { vendor_bill_id };

		const data = {
			search_type: 'invoice_payments',
			filter_by,
		};

		if (pagination) {
			data.pagination = pagination;
		}

		const CREDENTIALS = {
			url: '/search',
			data,
			setErrors,
		};

		return API.common(CREDENTIALS).then((response) => ({
			data: response?.data || [],
			rows: response?.data || [],
			meta: response?.meta || {},
		}));
	};

/**
 * Search Invoice Payments V2
 * POST /api/v2/search
 * { search_type: "invoice_payments", filter_by: { vendor_bill_id }, pagination: {...} }
 */
export const searchInvoicePaymentsV2 =
	({ vendor_bill_id, pagination, setErrors }) =>
	async () => {
		if (!vendor_bill_id) {
			toaster.error(VENDOR_BILL_ID_REQUIRED_MSG);
			return Promise.reject(new Error(VENDOR_BILL_ID_REQUIRED_MSG));
		}

		const filter_by = { vendor_bill_id };

		const data = {
			search_type: 'invoice_payments',
			filter_by,
		};

		if (pagination) {
			data.pagination = pagination;
		}

		const CREDENTIALS = {
			url: '/search',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => ({
			data: response?.data || [],
			rows: response?.data || [],
			meta: response?.meta || {},
		}));
	};

/**
 * Unified function to search invoice payments (handles both v1 and v2)
 * @param {Object} params - Parameters object
 * @param {number|string} params.vendor_bill_id - Vendor bill ID (required)
 * @param {Object} params.pagination - Pagination object with page and per_page (optional)
 * @param {Function} params.setErrors - Error handler function (optional)
 * @param {boolean} params.useV2API - Whether to use v2 API (default: false)
 * @returns {Promise} API response
 */
export const searchInvoicePayments =
	({ vendor_bill_id, pagination, setErrors, useV2API = false }) =>
	async () => {
		if (useV2API) {
			return searchInvoicePaymentsV2({ vendor_bill_id, pagination, setErrors })();
		}
		return searchInvoicePaymentsV1({ vendor_bill_id, pagination, setErrors })();
	};

/**
 * Create Invoice Payment V1
 * POST /api/v1/create
 * { create_type: "invoice_payment", data: { vendor_bill_id, paid_amount, utr_number, paid_date, status, document_key } }
 *
 * Records a new payment entry against a vendor bill.
 * Blocking: Cannot add payment if any existing payment has status = "pending"
 * Overpayment Guard: paid_amount cannot exceed remaining balance
 */
export const createInvoicePaymentV1 =
	({ req_data, setErrors }) =>
	async () => {
		if (!req_data || !req_data.vendor_bill_id) {
			toaster.error(VENDOR_BILL_ID_REQUIRED_MSG);
			return Promise.reject(new Error(VENDOR_BILL_ID_REQUIRED_MSG));
		}

		const data = {
			create_type: 'invoice_payment',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.status === 'success') {
				toaster.success(res.message || 'Invoice payment created successfully');
			} else if (res?.status === 'error') {
				toaster.error(res?.error?.message || 'Failed to create invoice payment');
			}
			return res;
		});
	};

/**
 * Create Invoice Payment V2
 * POST /api/v2/create
 * { create_type: "invoice_payment", data: { vendor_bill_id, paid_amount, utr_number, paid_date, image_keys (optional) } }
 *
 * Records a new payment entry against a vendor bill.
 * Blocking: Cannot add payment if any existing payment has status = "pending"
 * Overpayment Guard: paid_amount cannot exceed remaining balance
 */
export const createInvoicePaymentV2 =
	({ req_data, setErrors }) =>
	async () => {
		if (!req_data || !req_data.vendor_bill_id) {
			toaster.error(VENDOR_BILL_ID_REQUIRED_MSG);
			return Promise.reject(new Error(VENDOR_BILL_ID_REQUIRED_MSG));
		}

		const data = {
			create_type: 'invoice_payment',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.status === 'success') {
				toaster.success(res.message || 'Invoice payment created successfully');
			} else if (res?.status === 'error') {
				toaster.error(res?.error?.message || 'Failed to create invoice payment');
			}
			return res;
		});
	};

/**
 * Bulk Create Invoice Payment (V2)
 * POST /api/v2/create
 * { create_type: "bulk_invoice_payment", data: { vendor_bill_ids: [...], utr_number, paid_date, image_keys (optional) } }
 *
 * Pays every selected vendor bill for its full remaining balance using one shared
 * UTR + paid date. All-or-nothing: if any selected bill is missing/soft-deleted or
 * already fully paid, the whole batch is rejected (nothing written).
 * On success returns { success_count, payments: [{ vendor_bill_id, invoice_payment_id, paid_amount }] }.
 */
export const bulkCreateInvoicePaymentV2 =
	({ vendor_bill_ids, utr_number, paid_date, image_keys, setErrors }) =>
	async () => {
		if (!Array.isArray(vendor_bill_ids) || vendor_bill_ids.length === 0) {
			toaster.error('Please select at least one invoice');
			return Promise.reject(new Error('No invoices selected'));
		}

		const data = {
			create_type: 'bulk_invoice_payment',
			data: {
				vendor_bill_ids,
				utr_number,
				paid_date,
				...(image_keys?.length && { image_keys }),
			},
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.status === 'error') {
				toaster.error(res?.error?.message || 'Failed to record bulk invoice payment');
			}
			return res;
		});
	};

/**
 * Unified function to create invoice payment (handles both v1 and v2)
 * @param {Object} params - Parameters object
 * @param {Object} params.req_data - Request data
 *   - V1: { vendor_bill_id, paid_amount, utr_number, paid_date, status (optional), document_key (optional) }
 *   - V2: { vendor_bill_id, paid_amount, utr_number, paid_date, image_keys (optional array) } — no status field
 * @param {Function} params.setErrors - Error handler function (optional)
 * @param {boolean} params.useV2API - Whether to use v2 API (default: false)
 * @returns {Promise} API response
 */
export const createInvoicePayment =
	({ req_data, setErrors, useV2API = false }) =>
	async () => {
		if (useV2API) {
			return createInvoicePaymentV2({ req_data, setErrors })();
		}
		return createInvoicePaymentV1({ req_data, setErrors })();
	};

export default vendorBillApprovalSlice.reducer;
